import { fetchEventSource } from '@microsoft/fetch-event-source';
import type { QueryClient } from '@tanstack/react-query';
import { useAuth } from '../store/auth';

const API_BASE = (import.meta.env.VITE_API_URL ?? '') + '/api';

let abort: AbortController | null = null;

const KEYS_TO_INVALIDATE: readonly (readonly unknown[])[] = [
  ['my-room-invites-count'],
  ['my-room-notifications-count'],
  ['my-room-invites'],
  ['my-room-notifications'],
  ['my-rooms'],
];

function invalidate(qc: QueryClient) {
  for (const key of KEYS_TO_INVALIDATE) {
    qc.invalidateQueries({ queryKey: key as unknown[] });
  }
}

class FatalAuthError extends Error {}

export function startUserStream(qc: QueryClient) {
  stopUserStream();
  const token = useAuth.getState().token;
  if (!token) return;

  abort = new AbortController();

  fetchEventSource(`${API_BASE}/me/stream`, {
    signal: abort.signal,
    headers: { Authorization: `Bearer ${token}` },
    openWhenHidden: true,
    async onopen(response) {
      if (response.status === 401) {
        useAuth.getState().expireSession();
        throw new FatalAuthError('unauthorized');
      }
      if (!response.ok) {
        throw new Error(`stream open failed: ${response.status}`);
      }
    },
    onmessage(msg) {
      if (msg.event === 'refresh') invalidate(qc);
    },
    onerror(err) {
      if (err instanceof FatalAuthError) throw err;
      // Returning undefined lets fetch-event-source retry with its built-in backoff.
    },
  }).catch(() => {
    // Swallow terminal errors — token expiry already triggered logout above; other
    // failures will be picked up by the polling fallback.
  });
}

export function stopUserStream() {
  abort?.abort();
  abort = null;
}
